// src/components/snapshot/strategy-brief/StrategyBriefHero.tsx

import { Sparkles } from 'lucide-react';

type Props = {
  title: string;
  subtitle: string;
  executiveSummary: string;
  clientName: string;
  preparedDate: string;
};

function ClientBadge({ name }: { name: string }) {
  // Derive initials from client name (up to 3 words → 2–3 chars)
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 3)
    .map((w) => w[0].toUpperCase())
    .join('');

  return (
    <div className="flex items-center gap-2.5">
      <div className="h-9 w-9 rounded-xl bg-white/15 ring-1 ring-white/25 flex items-center justify-center flex-shrink-0">
        <span className="text-[11px] font-extrabold text-white tracking-wide">{initials}</span>
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold text-white/60 uppercase tracking-wider leading-none">
          Prepared for
        </div>
        <div className="text-sm font-bold text-white leading-snug mt-0.5 whitespace-nowrap">
          {name}
        </div>
      </div>
    </div>
  );
}

export function StrategyBriefHero({ title, subtitle, executiveSummary, clientName, preparedDate }: Props) {
  return (
    <section className="rounded-2xl border border-[#E5E7EB] bg-white shadow-sm overflow-hidden break-inside-avoid">
      <div className="px-6 py-5 sm:px-7 sm:py-6 bg-gradient-to-br from-[#18365E] to-[#0E213A]">

        {/* Top row: DVA branding left — client badge right */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-4.5 w-4.5 text-white" />
            </div>
            <div className="text-[11px] font-semibold tracking-wider uppercase text-white/70">
              Executive Artifact
            </div>
          </div>
          <ClientBadge name={clientName} />
        </div>

        {/* Title block */}
        <div className="mt-4">
          <h1 className="text-[26px] sm:text-[30px] font-extrabold tracking-tight text-white leading-tight">
            {title}
          </h1>
          <div className="flex items-center justify-between gap-4 mt-1.5">
            <p className="text-sm text-white/75 font-medium leading-snug">
              {subtitle}
            </p>
            <div className="text-[11px] text-white/50 font-medium whitespace-nowrap flex-shrink-0">
              {preparedDate}
            </div>
          </div>
        </div>

      </div>

      {/* Executive summary */}
      <div className="px-6 py-4 sm:px-7 sm:py-5">
        <div className="text-[11px] font-semibold tracking-wide uppercase text-[#717182]">
          Executive Summary
        </div>
        <p className="text-sm text-[#18365E] leading-relaxed font-medium mt-1.5">
          {executiveSummary}
        </p>
      </div>
    </section>
  );
}
